/**
 * 福岡県 名物めし 12段階（コンテンツデータ）。
 *
 * ここはコンテンツ専用ファイル。ゲームロジックからは
 * StageDefinition 型を通してのみ参照される。
 *
 * 本番画像を用意したら public/assets/foods/fukuoka/ に置き、
 * 各アイテムの image のパスを合わせるだけで差し替わる。
 * （ファイルが無い場合は自動的に仮アイコンで動作する）
 */

import { attachHulls } from '../../utils/itemShape';
import type { RewardLink, StageDefinition, StageItemDefinition } from '../types';

import { FUKUOKA_FOOD_HULLS } from './fukuokaFoodShapes.generated';
import { fukuokaSceneryTour } from './fukuokaScenes';

/**
 * 「食べに行く」導線の既定リンク。
 *
 * 実在の店名をデータへ直接書くと情報がすぐ古くなり、特定店の宣伝にもなってしまう。
 * そのため既定では「エリア × 料理名」の検索リンクにしている。
 * 提携店・掲載店が決まったら、該当アイテムの reward.links を実URLへ
 * 書き換えるだけでそのまま送客導線になる。
 */
function searchLinks(area: string, dish: string): RewardLink[] {
  return [
    {
      label: '地図で探す',
      url: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${area} ${dish}`)}`,
    },
    {
      label: 'お店を調べる',
      url: `https://www.google.com/search?q=${encodeURIComponent(`${area} ${dish}`)}`,
    },
  ];
}

const FOOD_ITEMS: StageItemDefinition[] = [
  {
    id: 'amaou',
    level: 1,
    name: 'あまおう',
    shortName: 'あまおう',
    image: '/assets/foods/fukuoka/01_amaou.webp',
    radius: 17,
    mass: 1,
    score: 2,
    description: '福岡県産の大粒いちご。',
    source: 'https://www.crossroadfukuoka.jp/spot/11377',
    icon: { label: '苺', shape: 'berry', baseColor: '#e0483f', accentColor: '#6fa84f' },
    reward: {
      unlockScore: 200,
      area: '福岡県内（博多あまおう）',
      headline: '「あかい・まるい・おおきい・うまい」の四拍子。',
      tip: '福岡県内限定で生産されるブランド。旬は12月〜4月、いちご狩り農園なら摘みたてを。',
      links: searchLinks('福岡県', 'あまおう いちご狩り'),
    },
  },
  {
    id: 'umegae-mochi',
    level: 2,
    name: '梅ヶ枝餅',
    shortName: '梅ヶ枝餅',
    image: '/assets/foods/fukuoka/02_umegae_mochi.webp',
    radius: 21,
    mass: 1.3,
    score: 4,
    description: '香ばしく焼いた餡入りの焼き餅。',
    source: 'https://www.city.dazaifu.lg.jp/site/kanko/16906.html',
    icon: { label: '餅', shape: 'mochi', baseColor: '#f6efe1', accentColor: '#c08a4a' },
    reward: {
      unlockScore: 500,
      area: '太宰府天満宮 参道',
      headline: '焼きたてのパリッと香ばしい皮と、やさしい粒あん。',
      tip: '参道周辺に30軒以上。焼きたてを1個から買える。冷めたら軽く焼き直すと復活。',
      links: searchLinks('太宰府天満宮 参道', '梅ヶ枝餅'),
    },
  },
  {
    id: 'gobouten-udon',
    level: 3,
    name: 'ごぼう天うどん',
    shortName: 'ごぼう天',
    image: '/assets/foods/fukuoka/03_gobouten_udon.webp',
    radius: 26,
    mass: 1.7,
    score: 8,
    description: 'やわ麺にごぼう天をのせた一杯。',
    source: 'https://www.crossroadfukuoka.jp/feature/udon-story',
    icon: { label: '饂飩', shape: 'noodle-bowl', baseColor: '#ecdfc0', accentColor: '#8a6a3b' },
    reward: {
      unlockScore: 1000,
      area: '博多',
      headline: 'やわらかい麺とあごだし。福岡のうどんはやさしい。',
      tip: '丸天との「かけあわせ」も定番。朝うどんもおすすめ。',
      links: searchLinks('博多', 'ごぼう天うどん'),
    },
  },
  {
    id: 'mentaiko',
    level: 4,
    name: '明太子',
    shortName: '明太子',
    image: '/assets/foods/fukuoka/04_mentaiko.webp',
    radius: 30,
    mass: 2.2,
    score: 16,
    description: 'ピリ辛の辛子明太子。',
    source: 'https://www.crossroadfukuoka.jp/feature/mentaiko',
    icon: { label: '明', shape: 'roe', baseColor: '#dc5f62', accentColor: '#8f2b2f' },
    reward: {
      unlockScore: 1800,
      area: '博多・中洲',
      headline: '1949年に博多・中洲で生まれた、博多土産の代表格。',
      tip: '食べ比べなら明太子専門店の量り売りが楽しい。',
      links: searchLinks('博多 中洲', '明太子'),
    },
  },
  {
    id: 'kashiwa-meshi',
    level: 5,
    name: 'かしわめし',
    shortName: 'かしわめし',
    image: '/assets/foods/fukuoka/05_kashiwa_meshi.webp',
    radius: 36,
    mass: 2.8,
    score: 32,
    description: '鶏の旨みが染みた炊き込みごはん。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/kashiwa_meshi_fukuoka.html',
    icon: { label: '鶏飯', shape: 'rice-bowl', baseColor: '#dda94a', accentColor: '#6b4a2f' },
    reward: {
      unlockScore: 3000,
      area: '福岡県内（駅弁は折尾）',
      headline: '鶏だしで炊いたごはんに、鶏そぼろ・錦糸卵・海苔。',
      tip: '県内全域の家庭や食堂で親しまれてきた一品。折尾の駅弁も有名。',
      links: searchLinks('福岡 折尾', 'かしわめし'),
    },
  },
  {
    id: 'teppan-gyoza',
    level: 6,
    name: '鉄鍋餃子',
    shortName: '鉄鍋餃子',
    image: '/assets/foods/fukuoka/06_tetsunabe_gyoza.webp',
    radius: 42,
    mass: 3.5,
    score: 64,
    description: '鉄鍋のままでる一口餃子。',
    source: 'https://www.crossroadfukuoka.jp/spot/14961',
    icon: { label: '餃子', shape: 'dumpling', baseColor: '#d8a45c', accentColor: '#4a3524' },
    reward: {
      unlockScore: 4500,
      area: '八幡・北九州',
      headline: '鉄の街・八幡で生まれた一口餃子。熱々の鉄鍋のまま出てくる。',
      tip: '柚子胡椒をちょんと乗せるのが福岡流。',
      links: searchLinks('北九州 八幡', '鉄なべ餃子'),
    },
  },
  {
    id: 'yaki-curry',
    level: 7,
    name: '焼きカレー',
    shortName: '焼きカレー',
    image: '/assets/foods/fukuoka/07_yaki_curry.webp',
    radius: 46,
    mass: 4.4,
    score: 128,
    description: 'チーズをのせて焼いたカレー。',
    source: 'https://kitakyushucity.guide/gourmets/foods-01',
    icon: { label: '焼咖', shape: 'curry-plate', baseColor: '#c8791f', accentColor: '#f4dd94' },
    reward: {
      unlockScore: 6500,
      area: '門司港レトロ',
      headline: 'チーズと卵をのせてオーブンで焼き上げる港町の名物。',
      tip: 'レトロ建築の街並みを歩いてから食べると倍おいしい。',
      links: searchLinks('門司港レトロ', '焼きカレー'),
    },
  },
  {
    id: 'hakata-tonkotsu-ramen',
    level: 8,
    name: '博多豚骨ラーメン',
    shortName: '豚骨ラーメン',
    image: '/assets/foods/fukuoka/08_tonkotsu_ramen.webp',
    radius: 51,
    mass: 5.4,
    score: 256,
    description: '細麺の白湯スープラーメン。',
    source: 'https://www.crossroadfukuoka.jp/feature/noodles',
    icon: { label: '豚骨', shape: 'ramen', baseColor: '#f4ead6', accentColor: '#b8452f' },
    reward: {
      unlockScore: 9000,
      area: '博多・長浜・中洲',
      headline: '極細ストレート麺と濃厚白湯。〆の一杯はこれ。',
      tip: '麺のかたさは「バリカタ」から。替え玉は必須。',
      links: searchLinks('博多 長浜', '豚骨ラーメン'),
    },
  },
  {
    id: 'goma-saba',
    level: 9,
    name: 'ごまさば',
    shortName: 'ごまさば',
    image: '/assets/foods/fukuoka/09_goma_saba.webp',
    radius: 57,
    mass: 6.6,
    score: 512,
    description: '新鮮な鯖のごま醤油和え。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/hakatano_gomasaba_fukuoka.html',
    icon: { label: '鯖', shape: 'fish', baseColor: '#6d8fa8', accentColor: '#2f3a44' },
    reward: {
      unlockScore: 12000,
      area: '博多・長浜',
      headline: '生の鯖を食べられるのは玄界灘のそばならでは。',
      tip: '残ったらだし茶漬けにしてもらうと二度おいしい。',
      links: searchLinks('博多 長浜', 'ごまさば'),
    },
  },
  {
    id: 'motsunabe',
    level: 10,
    name: 'もつ鍋',
    shortName: 'もつ鍋',
    image: '/assets/foods/fukuoka/10_motsunabe.webp',
    radius: 63,
    mass: 8,
    score: 1024,
    description: 'にらとキャベツたっぷりのもつ鍋。',
    source: 'https://www.crossroadfukuoka.jp/spot/12669',
    icon: { label: 'もつ', shape: 'hotpot', baseColor: '#c2452f', accentColor: '#6d9a3f' },
    reward: {
      unlockScore: 16000,
      area: '博多',
      headline: 'ぷりぷりのもつと山盛りのニラ。醤油か味噌かは好みで。',
      tip: '〆はちゃんぽん麺。唐辛子とにんにくはお好みで増量。',
      links: searchLinks('博多', 'もつ鍋'),
    },
  },
  {
    id: 'mizutaki',
    level: 11,
    name: '水炊き',
    shortName: '水炊き',
    image: '/assets/foods/fukuoka/11_mizutaki.webp',
    radius: 70,
    mass: 9.6,
    score: 2048,
    description: '鶏の旨みを味わう鍋料理。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/wakadori_no_mizutaki_fukuoka.html',
    icon: { label: '水炊', shape: 'pot', baseColor: '#eef1e6', accentColor: '#7f9e5c' },
    reward: {
      unlockScore: 22000,
      area: '博多',
      headline: 'まずは白濁スープを一杯。それだけでごちそう。',
      tip: '博多祇園山笠の直会でも食される一品。〆は雑炊かちゃんぽん麺。老舗は予約推奨。',
      links: searchLinks('博多', '水炊き'),
    },
  },
  {
    id: 'genkai-torafugu',
    level: 12,
    name: '玄界灘のとらふぐ てっさ',
    shortName: 'とらふぐ',
    image: '/assets/foods/fukuoka/12_torafugu.webp',
    radius: 78,
    mass: 11.4,
    score: 4096,
    description: '玄界灘のとらふぐを大皿へ菊花盛りにした、福岡の冬のごちそう。',
    source: 'https://www.crossroadfukuoka.jp/event/11976',
    icon: {
      label: '河豚',
      shape: 'sashimi-platter',
      baseColor: '#f8f4ec',
      accentColor: '#c2455a',
    },
    reward: {
      unlockScore: 30000,
      area: '鐘崎（宗像）・玄界灘',
      headline: '天然とらふぐの県内最大の水揚げ港・鐘崎から。皿の絵が透けるほど薄く引く。',
      tip: '旬は12月〜3月。てっさ・唐揚げ・てっちりのコースで、〆は必ずふぐ雑炊。',
      links: searchLinks('福岡 宗像', 'とらふぐ 料理'),
    },
  },
];

/** 得点は 2 ^ level を基準にしている（GAME_TUNING.score で方式変更可能）。 */
export const fukuokaFoodStage: StageDefinition = {
  regionId: 'fukuoka',
  regionName: '福岡県',
  mode: 'food',
  title: '福岡めしマージ',
  theme: {
    primaryColor: '#7a1f24',
    secondaryColor: '#d98324',
    accentColor: '#f2c14e',
    backgroundColor: '#2b1a17',
  },
  // 音は全県共通（config/audio.ts）。福岡だけ変えたくなったら audio: { bgm: ... } で上書きする
  /**
   * 背景写真。ファイルを置けば実写背景へ切り替わり、
   * 無ければ手続き生成の夜景がそのまま使われる（権利的に安全な素材のみ配置すること）。
   */
  scenery: {
    photos: {
      dusk: '/assets/scenery/fukuoka/01_dusk.jpg',
      evening: '/assets/scenery/fukuoka/02_evening.jpg',
      night: '/assets/scenery/fukuoka/03_night.jpg',
      deepnight: '/assets/scenery/fukuoka/04_deepnight.jpg',
      dawn: '/assets/scenery/fukuoka/05_dawn.jpg',
    },
    tour: fukuokaSceneryTour,
  },
  // 画像の輪郭（自動生成）を付与する。輪郭がある料理は丸以外の当たり判定になる。
  items: attachHulls(FOOD_ITEMS, FUKUOKA_FOOD_HULLS),
};
